const safeText = (value, limit = 300) =>
  typeof value === "string" ? value.replace(/\0/g, " ").trim().slice(0, limit) : "";

const finiteNumber = (value) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const normalizePoints = (raw, limit) =>
  Array.isArray(raw)
    ? raw.slice(0, limit).reduce((result, item) => {
        const period = safeText(item?.period, 20);
        const value = finiteNumber(item?.value);
        if (!period || value === null) return result;
        result.push({ period, value });
        return result;
      }, [])
    : [];

export const normalizeResponseProjections = (raw, citationIds = new Set()) => {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, 3).reduce((result, item) => {
    const id = safeText(item?.id, 32);
    if (!/^projection-[1-9][0-9]*$/.test(id) || item?.kind !== "utility_budget") {
      return result;
    }
    const resources = new Set();
    const series = Array.isArray(item?.series)
      ? item.series.slice(0, 3).reduce((items, entry) => {
          const resource = safeText(entry?.resource, 32);
          const baselineYear = Number(entry?.baseline_year);
          const projectionYear = Number(entry?.projection_year);
          const lower = finiteNumber(entry?.lower);
          const recommended = finiteNumber(entry?.recommended);
          const upper = finiteNumber(entry?.upper);
          const citationIdsForSeries = Array.isArray(entry?.citation_ids)
            ? entry.citation_ids.filter((citationId) => citationIds.has(citationId)).slice(0, 3)
            : [];
          const monthlyPlan = normalizePoints(entry?.monthly_plan, 12);
          if (
            !["electricity", "gas", "water"].includes(resource) ||
            resources.has(resource) ||
            !Number.isInteger(baselineYear) ||
            projectionYear !== baselineYear + 1 ||
            lower === null ||
            recommended === null ||
            upper === null ||
            lower > recommended ||
            recommended > upper ||
            monthlyPlan.length !== 12 ||
            citationIdsForSeries.length === 0
          ) {
            return items;
          }
          resources.add(resource);
          items.push({
            resource,
            label: safeText(entry?.label, 80) || resource,
            unit: safeText(entry?.unit, 40),
            baselineYear,
            projectionYear,
            baselineTotal: finiteNumber(entry?.baseline_total),
            recommended,
            lower,
            upper,
            growthAssumptionPercent: finiteNumber(entry?.growth_assumption_percent),
            method: safeText(entry?.method, 300),
            confidence: entry?.confidence === "directional" ? "directional" : "limited",
            annualActuals: normalizePoints(entry?.annual_actuals, 6),
            monthlyPlan,
            citationIds: citationIdsForSeries,
          });
          return items;
        }, [])
      : [];
    if (!series.length) return result;
    result.push({
      id,
      kind: "utility_budget",
      title: safeText(item?.title, 160) || "Evidence-based planning scenario",
      unit: safeText(item?.unit, 40),
      recommendedTotal: finiteNumber(item?.recommended_total),
      lowerTotal: finiteNumber(item?.lower_total),
      upperTotal: finiteNumber(item?.upper_total),
      series,
      excluded: Array.isArray(item?.excluded)
        ? item.excluded.slice(0, 3).map((gap) => ({
            resource: safeText(gap?.resource, 32),
            label: safeText(gap?.label, 80),
            reason: safeText(gap?.reason, 300),
          })).filter((gap) => gap.label && gap.reason)
        : [],
      assumptions: Array.isArray(item?.assumptions)
        ? item.assumptions.map((value) => safeText(value, 300)).filter(Boolean).slice(0, 8)
        : [],
      limitations: Array.isArray(item?.limitations)
        ? item.limitations.map((value) => safeText(value, 300)).filter(Boolean).slice(0, 8)
        : [],
    });
    return result;
  }, []);
};

const SERIES_COLORS = ["#3276c8", "#dd8a2e", "#45a37a"];

export const buildMonthlyProjectionChartOption = (projection) => ({
  animation: false,
  color: SERIES_COLORS,
  grid: { left: 12, right: 14, top: 36, bottom: 8, containLabel: true },
  legend: { top: 0, left: 0, itemWidth: 14, itemHeight: 8, textStyle: { color: "#53657a" } },
  tooltip: {
    trigger: "axis",
    valueFormatter: (value) =>
      new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value),
  },
  xAxis: {
    type: "category",
    boundaryGap: false,
    data: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    axisLine: { lineStyle: { color: "#d8e1ec" } },
    axisTick: { show: false },
    axisLabel: { color: "#718096", fontSize: 11 },
  },
  yAxis: {
    type: "value",
    axisLabel: {
      color: "#718096",
      formatter: (value) => new Intl.NumberFormat(undefined, { notation: "compact" }).format(value),
    },
    splitLine: { lineStyle: { color: "#edf1f6" } },
  },
  series: projection.series.map((item) => ({
    name: `${item.label} ${item.projectionYear}`,
    type: "line",
    smooth: 0.25,
    symbol: "circle",
    symbolSize: 5,
    lineStyle: { width: 2.5 },
    areaStyle: { opacity: 0.06 },
    data: item.monthlyPlan.map((point) => point.value),
  })),
});
